import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import APP_VERSION from "../version";

export default function TripPage({ currentUser }) {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [location, setLocation] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attending, setAttending] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [campers, setCampers] = useState([]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCrewIcon = (type, age, petType) => {
    if (type === "pet") {
      switch (petType) {
        case "Dog": return "/pet-image.png";
        case "Horse": return "/horse-image.png";
        case "Cat": return "/cat-image.png";
        case "Velociraptor": return "/velociraptor-image.png";
        default: return "/dog-image.png";
      }
    }
    switch (age) {
      case "baby": return "/baby-image.png";
      case "toddler": return "/toddler-image.png";
      case "child": return "/child-image.png";
      default: return "/camper-image.png";
    }
  };

  const getDrivetrainImage = (drivetrain, type) => {
    if (type === "car") {
      switch (drivetrain) {
        case "2 Wheel Drive": return "/2wd-image.png";
        case "All Wheel Drive": return "/awd-image.png";
        case "4 Wheel Drive": return "/4wd-image.png";
        default: return "/car-image.png";
      }
    }
    if (type === "boat") return "/boat-image.png";
    if (type === "trailer") return "/trailer-image.png";
    return null;
  };

  const isOwner = trip && currentUser && trip.creator_profile_id === currentUser.profile_id;

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [tripRes, campersRes, locationRes] = await Promise.all([
          fetch(`/api/trips/${tripId}`, { credentials: "include" }),
          fetch(`/api/trips/${tripId}/attendees`, { credentials: "include" }),
          fetch(`/api/trips/${tripId}/location`, { credentials: "include" }),
        ]);

        const tripData = await tripRes.json();
        const campersData = await campersRes.json();
        const locationData = await locationRes.json();

        if (tripRes.ok) {
          setTrip(tripData);
          setAttending(tripData.attending);
          setStartDate(tripData.start_date);
          setEndDate(tripData.end_date);
        } else {
          console.error("Error loading trip:", tripData.error);
        }

        if (campersRes.ok) {
          setCampers(campersData.attendees || []);
        } else {
          console.error("Error loading campers:", campersData.error);
        }

        if (locationRes.ok) {
          setLocation(locationData.location);
          setFeatures(locationData.features || []);
        } else {
          console.error("Error loading location:", locationData.error);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [tripId]);

  const handleAttendanceChange = async (value) => {
    if (value === true) {
      navigate(`/trip/${tripId}/attendance`);
    } else {
      try {
        await fetch(`/api/trips/${tripId}/attending`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ attending: value }),
        });
        setAttending(value);
      } catch (err) {
        console.error("Error updating attendance:", err);
      }
    }
  };

  const handleSaveDates = async () => {
    try {
      const res = await fetch(`/api/trips/${tripId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });
      if (res.ok) {
        setTrip((prev) => ({ ...prev, start_date: startDate, end_date: endDate }));
        setEditMode(false);
      } else {
        console.error("Failed to save dates");
      }
    } catch (err) {
      console.error("Error saving dates:", err);
    }
  };

  const handleLogout = async () => {
    await fetch("/auth/logout", { credentials: "include" });
    navigate("/login", { replace: true });
  };

  if (loading) return <p>Loading trip...</p>;
  if (!trip) return <p>Trip not found.</p>;

  const organiserMainCamper = campers.find(c => c.profile_id === trip.creator_profile_id);
  const organiserCamper = organiserMainCamper?.crew?.find(c => c.type === "camper");

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>{trip.trip_name}</h2>

        {isOwner ? (
          editMode ? (
            <>
              <label>Start Date:</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <label>End Date:</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              <button onClick={handleSaveDates}>Save</button>
              <button onClick={() => setEditMode(false)}>Cancel</button>
            </>
          ) : (
            <>
              <p><strong>Dates:</strong> {formatDate(trip.start_date)} → {formatDate(trip.end_date)}</p>
              <button onClick={() => setEditMode(true)}>Edit Dates</button>
            </>
          )
        ) : (
          <p><strong>Dates:</strong> {formatDate(trip.start_date)} → {formatDate(trip.end_date)}</p>
        )}

        <p><strong>Organiser:</strong> {organiserCamper ? organiserCamper.name : trip.creator_name}</p>

        {location && (
          <div
            style={{
              backgroundColor: "#fff",
              color: "#2c7373",
              padding: "16px",
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              marginTop: "20px",
              textAlign: "left",
            }}
          >
            <p style={{ margin: "2px 0" }}><strong>Location:</strong> {location.name}</p>
            <p style={{ margin: "2px 0" }}>
              <strong>Website:</strong>{" "}
              <a href={location.campsite_url} target="_blank" rel="noreferrer">
                {location.campsite_url}
              </a>
            </p>
            <p style={{ margin: "2px 0" }}><strong>Phone:</strong> {location.phone_contact}</p>
            <p style={{ margin: "2px 0" }}><strong>Address:</strong> {location.address}</p>
            <p style={{ margin: "2px 0" }}><strong>Pet friendly:</strong> {location.pet_friendly ? "Yes" : "No"}</p>
            <p style={{ margin: "2px 0" }}><strong>Access:</strong> {location.drivetrain}</p>
            {features.length > 0 && (
              <p style={{ margin: "2px 0" }}><strong>Features:</strong> {features.map(f => f.name).join(", ")}</p>
            )}
          </div>
        )}

        {!isOwner && attending === null && (
          <div style={{ marginTop: "15px" }}>
            <strong>Your Status:</strong><br />
            <button onClick={() => handleAttendanceChange(true)} style={{ marginRight: "10px" }}>
              ✅ Going
            </button>
            <button onClick={() => handleAttendanceChange(false)}>
              ❌ Not Going
            </button>
          </div>
        )}

        <div style={{ marginTop: "30px", textAlign: "left" }}>
          <h3>Campers</h3>
          {campers.map((camper, idx) => {
            const mainCamper = camper.crew?.find(c => c.type === "camper");
            const otherCrew = camper.crew?.filter(c => c !== mainCamper);

            return (
              <div
                key={idx}
                style={{
                  backgroundColor: "#fff",
                  color: "#2c7373",
                  padding: "16px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  marginBottom: "15px",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "1.1em",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <img src="/camper-image.png" alt="camper" style={{ width: "24px", height: "24px" }} />
                  {mainCamper ? mainCamper.name : camper.profile_name}
                </div>

                {otherCrew?.length > 0 && otherCrew.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      paddingLeft: "20px",
                      marginTop: "4px",
                    }}
                  >
                    <img
                      src={getCrewIcon(c.type, c.age, c.pet_type)}
                      alt={c.type}
                      style={{ width: "20px", height: "20px" }}
                    />
                    {c.name}
                  </div>
                ))}

                {camper.vehicles?.length > 0 && camper.vehicles.map((r) => {
                  let label = "";
                  if (r.type === "car") label = `${r.make} ${r.model} ${r.registration}`;
                  else if (r.type === "boat") label = r.boat_type || "Boat";
                  else if (r.type === "trailer") label = r.trailer_type || "Trailer";
                  else label = "Vehicle";

                  return (
                    <div
                      key={r.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        paddingLeft: "20px",
                        marginTop: "4px",
                      }}
                    >
                      <img
                        src={getDrivetrainImage(r.drivetrain, r.type)}
                        alt={r.type}
                        style={{ width: "20px", height: "20px" }}
                      />
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <button
          className="home-button"
          onClick={() => navigate("/")}
          style={{ marginTop: "30px", width: "100%" }}
        >
          Home
        </button>

        <div
          className="version-text"
          style={{
            marginTop: "20px",
            fontSize: "12px",
            color: "#ccc",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>
            Version: <strong>{APP_VERSION}</strong>
          </span>
          <span>
            <Link to="#" onClick={(e) => { e.preventDefault(); handleLogout(); }} className="contact-link" style={{ marginRight: "16px" }}>
              Logout
            </Link>
            <Link to="/contact" className="contact-link">Contact us</Link>
          </span>
        </div>
      </div>
    </div>
  );
}
